/**
 * NavigatorX Analytics Service
 * 
 * Enterprise-grade analytics for NavigatorX:
 * - Real-time user activity tracking
 * - DAU/WAU/MAU metrics
 * - Retention analysis with cohorts
 * - Feature usage tracking
 * - Funnel analytics
 * - Report generation
 * 
 * @author Claude AI for TGE.LA
 * @version 1.0.0
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  increment,
  collectionGroup,
  startAfter,
  endBefore
} from 'firebase/firestore';
import { navDb } from '../firebase/client';
import { NAV_USERS_COLLECTION } from './navigatorXService';

// ============================================================================
// COLLECTION CONSTANTS
// ============================================================================

const ANALYTICS_COLLECTION = 'nav_analytics';
const EVENTS_COLLECTION = 'nav_events';
const DAILY_STATS_DOC = 'daily_stats';
const WEEKLY_STATS_DOC = 'weekly_stats';
const MONTHLY_STATS_DOC = 'monthly_stats';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface DailyActiveUsers {
  date: string; // YYYY-MM-DD
  count: number;
  uniqueUserIds: string[];
}

export interface UsageMetrics {
  dau: number; // Daily Active Users
  wau: number; // Weekly Active Users
  mau: number; // Monthly Active Users
  dauWauRatio: number; // Stickiness metric
  avgSessionDuration: number; // In minutes
  avgSessionsPerUser: number;
  totalSessions: number;
}

export interface FeatureUsage {
  feature: string;
  usageCount: number;
  uniqueUsers: number;
  avgUsagePerUser: number;
  percentOfUsers: number;
}

export interface RetentionData {
  cohortDate: string;
  cohortSize: number;
  retentionByDay: { day: number; retained: number; percentage: number }[];
}

export interface FunnelStep {
  name: string;
  count: number;
  conversionRate: number;
  dropOffRate: number;
}

export interface RealtimeStats {
  activeUsers: number;
  activeNavigations: number;
  recentEvents: AnalyticsEvent[];
  updatedAt: Timestamp;
}

export interface AnalyticsEvent {
  id?: string;
  userId: string;
  eventType: string;
  eventData?: Record<string, any>;
  timestamp: Timestamp;
  sessionId?: string;
  deviceInfo?: {
    platform: string;
    version: string;
    model?: string;
  };
}

export interface ScreenAnalytics {
  screenName: string;
  views: number;
  uniqueUsers: number;
  avgTimeOnScreen: number; // seconds
  bounceRate: number;
}

export interface SearchAnalytics {
  term: string;
  count: number;
  resultsFound: number;
  clickThroughRate: number;
}

export interface AnalyticsReport {
  id?: string;
  name: string;
  type: 'daily' | 'weekly' | 'monthly' | 'custom';
  metrics: string[];
  filters?: Record<string, any>;
  schedule?: {
    frequency: 'daily' | 'weekly' | 'monthly';
    time: string;
    recipients: string[];
  };
  createdAt: Timestamp;
  createdBy: string;
  lastRunAt?: Timestamp;
}

// ============================================================================
// DATE HELPERS
// ============================================================================

function getDateString(date: Date = new Date()): string {
  return date.toISOString().split('T')[0];
}

function getWeekStart(date: Date = new Date()): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function getMonthStart(date: Date = new Date()): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function getDaysAgo(days: number): Date {
  const d = new Date();
  d.setDate(d.getDate() - days);
  d.setHours(0, 0, 0, 0);
  return d;
}

// ============================================================================
// EVENT TRACKING
// ============================================================================

/**
 * Track an analytics event
 */
export async function trackEvent(event: Omit<AnalyticsEvent, 'id' | 'timestamp'>): Promise<void> {
  await addDoc(collection(navDb, EVENTS_COLLECTION), {
    ...event,
    timestamp: Timestamp.now()
  });
  
  // Update daily active users
  await updateDailyActiveUser(event.userId);
}

/**
 * Update daily active user count
 */
async function updateDailyActiveUser(userId: string): Promise<void> {
  const today = getDateString();
  const docRef = doc(navDb, ANALYTICS_COLLECTION, `dau_${today}`);
  
  try {
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      if (!data.userIds?.includes(userId)) {
        await updateDoc(docRef, {
          count: increment(1),
          userIds: [...(data.userIds || []), userId]
        });
      }
    } else {
      await setDoc(docRef, {
        date: today,
        count: 1,
        userIds: [userId]
      });
    }
  } catch (error) {
    console.error('Error updating DAU:', error);
  }
}

/**
 * Get recent events
 */
export async function getRecentEvents(limitCount: number = 50): Promise<AnalyticsEvent[]> {
  const q = query(
    collection(navDb, EVENTS_COLLECTION),
    orderBy('timestamp', 'desc'),
    limit(limitCount)
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as AnalyticsEvent[];
}

/**
 * Get events for a specific user
 */
export async function getUserEvents(
  userId: string,
  limitCount: number = 100
): Promise<AnalyticsEvent[]> {
  const q = query(
    collection(navDb, EVENTS_COLLECTION),
    where('userId', '==', userId),
    orderBy('timestamp', 'desc'),
    limit(limitCount)
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as AnalyticsEvent[];
}

// ============================================================================
// USAGE METRICS
// ============================================================================

/**
 * Get comprehensive usage metrics
 */
export async function getUsageMetrics(): Promise<UsageMetrics> {
  const today = new Date();
  const weekStart = getWeekStart(today);
  const monthStart = getMonthStart(today);
  
  // Get DAU
  const dauDoc = await getDoc(doc(navDb, ANALYTICS_COLLECTION, `dau_${getDateString()}`));
  const dau = dauDoc.exists() ? dauDoc.data().count || 0 : 0;
  
  // Calculate WAU (last 7 days)
  const wauUserIds = new Set<string>();
  for (let i = 0; i < 7; i++) {
    const date = getDaysAgo(i);
    const dayDoc = await getDoc(doc(navDb, ANALYTICS_COLLECTION, `dau_${getDateString(date)}`));
    if (dayDoc.exists()) {
      (dayDoc.data().userIds || []).forEach((id: string) => wauUserIds.add(id));
    }
  }
  const wau = wauUserIds.size;
  
  // Calculate MAU (last 30 days)
  const mauUserIds = new Set<string>();
  for (let i = 0; i < 30; i++) {
    const date = getDaysAgo(i);
    const dayDoc = await getDoc(doc(navDb, ANALYTICS_COLLECTION, `dau_${getDateString(date)}`));
    if (dayDoc.exists()) {
      (dayDoc.data().userIds || []).forEach((id: string) => mauUserIds.add(id));
    }
  }
  const mau = mauUserIds.size;
  
  // Calculate stickiness (DAU/MAU ratio)
  const dauWauRatio = wau > 0 ? (dau / wau) * 100 : 0;
  
  // Get session data from events (approximate)
  const sessionsDoc = await getDoc(doc(navDb, ANALYTICS_COLLECTION, 'session_stats'));
  const sessionStats = sessionsDoc.exists() ? sessionsDoc.data() : {
    avgDuration: 0,
    avgPerUser: 0,
    total: 0
  };
  
  return {
    dau,
    wau,
    mau,
    dauWauRatio: Math.round(dauWauRatio * 100) / 100,
    avgSessionDuration: sessionStats.avgDuration || 0,
    avgSessionsPerUser: sessionStats.avgPerUser || 0,
    totalSessions: sessionStats.total || 0
  };
}

/**
 * Get DAU history for charting
 */
export async function getDauHistory(days: number = 30): Promise<{ date: string; count: number }[]> {
  const history: { date: string; count: number }[] = [];
  
  for (let i = days - 1; i >= 0; i--) {
    const date = getDaysAgo(i);
    const dateStr = getDateString(date);
    const dayDoc = await getDoc(doc(navDb, ANALYTICS_COLLECTION, `dau_${dateStr}`));
    
    history.push({
      date: dateStr,
      count: dayDoc.exists() ? dayDoc.data().count || 0 : 0
    });
  }
  
  return history;
}

// ============================================================================
// FEATURE USAGE
// ============================================================================

/**
 * Track feature usage
 */
export async function trackFeatureUsage(
  userId: string,
  feature: string,
  metadata?: Record<string, any>
): Promise<void> {
  const today = getDateString();
  const featureDocRef = doc(navDb, ANALYTICS_COLLECTION, `feature_${feature}_${today}`);
  
  try {
    const docSnap = await getDoc(featureDocRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      const userIds = data.userIds || [];
      const isNewUser = !userIds.includes(userId);
      
      await updateDoc(featureDocRef, {
        usageCount: increment(1),
        uniqueUsers: isNewUser ? increment(1) : increment(0),
        userIds: isNewUser ? [...userIds, userId] : userIds
      });
    } else {
      await setDoc(featureDocRef, {
        feature,
        date: today,
        usageCount: 1,
        uniqueUsers: 1,
        userIds: [userId]
      });
    }
  } catch (error) {
    console.error('Error tracking feature usage:', error);
  }
}

/**
 * Get feature usage statistics
 */
export async function getFeatureUsageStats(days: number = 7): Promise<FeatureUsage[]> {
  const features = ['navigation', 'search', 'favorites', 'hazards', 'voice', 'offline'];
  const stats: FeatureUsage[] = [];
  
  // Get total users for percentage calculation
  const usersSnap = await getDocs(collection(navDb, NAV_USERS_COLLECTION));
  const totalUsers = usersSnap.docs.filter(d => 
    !['blocked', 'invites', 'admin_logs'].includes(d.id)
  ).length;
  
  for (const feature of features) {
    let totalUsage = 0;
    const uniqueUserIds = new Set<string>();
    
    for (let i = 0; i < days; i++) {
      const date = getDateString(getDaysAgo(i));
      const featureDoc = await getDoc(doc(navDb, ANALYTICS_COLLECTION, `feature_${feature}_${date}`));
      
      if (featureDoc.exists()) {
        const data = featureDoc.data();
        totalUsage += data.usageCount || 0;
        (data.userIds || []).forEach((id: string) => uniqueUserIds.add(id));
      }
    }
    
    const uniqueUsers = uniqueUserIds.size;
    
    stats.push({
      feature,
      usageCount: totalUsage,
      uniqueUsers,
      avgUsagePerUser: uniqueUsers > 0 ? Math.round(totalUsage / uniqueUsers * 10) / 10 : 0,
      percentOfUsers: totalUsers > 0 ? Math.round(uniqueUsers / totalUsers * 100) : 0
    });
  }
  
  return stats.sort((a, b) => b.usageCount - a.usageCount);
}

// ============================================================================
// RETENTION ANALYSIS
// ============================================================================

/**
 * Calculate retention for a cohort
 */
export async function getRetentionData(
  cohortDate: string,
  daysToTrack: number = 30
): Promise<RetentionData> {
  // Get users who signed up on the cohort date
  const cohortStart = new Date(cohortDate);
  cohortStart.setHours(0, 0, 0, 0);
  const cohortEnd = new Date(cohortDate);
  cohortEnd.setHours(23, 59, 59, 999);
  
  const usersQuery = query(
    collection(navDb, NAV_USERS_COLLECTION),
    where('createdAt', '>=', Timestamp.fromDate(cohortStart)),
    where('createdAt', '<=', Timestamp.fromDate(cohortEnd))
  );
  
  const cohortSnapshot = await getDocs(usersQuery);
  const cohortUserIds = cohortSnapshot.docs
    .filter(d => !['blocked', 'invites', 'admin_logs'].includes(d.id))
    .map(d => d.id);
  
  const cohortSize = cohortUserIds.length;
  const retentionByDay: { day: number; retained: number; percentage: number }[] = [];
  
  // Check retention for each day
  for (let day = 1; day <= daysToTrack; day++) {
    const checkDate = new Date(cohortDate);
    checkDate.setDate(checkDate.getDate() + day);
    const dateStr = getDateString(checkDate);
    
    const dauDoc = await getDoc(doc(navDb, ANALYTICS_COLLECTION, `dau_${dateStr}`));
    const activeUserIds = dauDoc.exists() ? (dauDoc.data().userIds || []) : [];
    
    const retained = cohortUserIds.filter(id => activeUserIds.includes(id)).length;
    
    retentionByDay.push({
      day,
      retained,
      percentage: cohortSize > 0 ? Math.round(retained / cohortSize * 100) : 0
    });
  }
  
  return {
    cohortDate,
    cohortSize,
    retentionByDay
  };
}

/**
 * Get multiple cohort retention data for comparison
 */
export async function getCohortRetentionMatrix(
  weeks: number = 8
): Promise<RetentionData[]> {
  const cohorts: RetentionData[] = [];
  
  for (let w = 0; w < weeks; w++) {
    const cohortDate = getDateString(getDaysAgo(w * 7));
    const retention = await getRetentionData(cohortDate, 7);
    cohorts.push(retention);
  }
  
  return cohorts;
}

// ============================================================================
// FUNNEL ANALYTICS
// ============================================================================

/**
 * Get signup funnel analytics
 */
export async function getSignupFunnel(days: number = 30): Promise<FunnelStep[]> {
  // Count users at each step
  const startDate = getDaysAgo(days);
  
  const usersQuery = query(
    collection(navDb, NAV_USERS_COLLECTION),
    where('createdAt', '>=', Timestamp.fromDate(startDate))
  );
  const usersSnap = await getDocs(usersQuery);
  const users = usersSnap.docs
    .filter(d => !['blocked', 'invites', 'admin_logs'].includes(d.id))
    .map(d => ({ id: d.id, ...d.data() }));
  
  // Define funnel steps
  const steps = [
    { name: 'App Opened', check: () => users.length },
    { name: 'Signup Started', check: () => users.filter((u: any) => u.authProvider).length },
    { name: 'Signup Completed', check: () => users.filter((u: any) => u.createdAt).length },
    { name: 'Trial Started', check: () => users.filter((u: any) => u.trialStartedAt).length },
    { name: 'First Navigation', check: () => users.filter((u: any) => u.totalNavigations > 0).length },
    { name: 'Converted to Paid', check: () => users.filter((u: any) => 
      u.subscriptionStatus === 'SUBSCRIBED' || u.subscriptionStatus === 'active'
    ).length }
  ];
  
  const funnel: FunnelStep[] = [];
  let prevCount = 0;
  
  for (let i = 0; i < steps.length; i++) {
    const count = steps[i].check();
    const conversionRate = i === 0 ? 100 : (prevCount > 0 ? Math.round(count / prevCount * 100) : 0);
    const dropOffRate = i === 0 ? 0 : (100 - conversionRate);
    
    funnel.push({
      name: steps[i].name,
      count,
      conversionRate,
      dropOffRate
    });
    
    prevCount = count;
  }
  
  return funnel;
}

/**
 * Get navigation funnel analytics
 */
export async function getNavigationFunnel(days: number = 30): Promise<FunnelStep[]> {
  const startDate = getDaysAgo(days);
  
  // Get navigation events
  const eventsQuery = query(
    collection(navDb, EVENTS_COLLECTION),
    where('timestamp', '>=', Timestamp.fromDate(startDate)),
    where('eventType', 'in', ['search_initiated', 'well_selected', 'navigation_started', 'navigation_completed'])
  );
  
  const eventsSnap = await getDocs(eventsQuery);
  const events = eventsSnap.docs.map(d => d.data());
  
  const steps = [
    { name: 'Search Initiated', count: events.filter(e => e.eventType === 'search_initiated').length },
    { name: 'Well Selected', count: events.filter(e => e.eventType === 'well_selected').length },
    { name: 'Navigation Started', count: events.filter(e => e.eventType === 'navigation_started').length },
    { name: 'Navigation Completed', count: events.filter(e => e.eventType === 'navigation_completed').length }
  ];
  
  const funnel: FunnelStep[] = [];
  let prevCount = 0;
  
  for (let i = 0; i < steps.length; i++) {
    const count = steps[i].count;
    const conversionRate = i === 0 ? 100 : (prevCount > 0 ? Math.round(count / prevCount * 100) : 0);
    const dropOffRate = i === 0 ? 0 : (100 - conversionRate);
    
    funnel.push({
      name: steps[i].name,
      count,
      conversionRate,
      dropOffRate
    });
    
    prevCount = count;
  }
  
  return funnel;
}

// ============================================================================
// SCREEN ANALYTICS
// ============================================================================

/**
 * Get screen view analytics
 */
export async function getScreenAnalytics(days: number = 7): Promise<ScreenAnalytics[]> {
  const screens = ['HomeScreen', 'MapScreen', 'SearchScreen', 'FavoritesScreen', 'SettingsScreen', 'ProfileScreen'];
  const stats: ScreenAnalytics[] = [];
  
  for (const screen of screens) {
    let totalViews = 0;
    const uniqueUsers = new Set<string>();
    let totalTime = 0;
    let bounces = 0;
    
    for (let i = 0; i < days; i++) {
      const date = getDateString(getDaysAgo(i));
      const screenDoc = await getDoc(doc(navDb, ANALYTICS_COLLECTION, `screen_${screen}_${date}`));
      
      if (screenDoc.exists()) {
        const data = screenDoc.data();
        totalViews += data.views || 0;
        (data.userIds || []).forEach((id: string) => uniqueUsers.add(id));
        totalTime += data.totalTimeMs || 0;
        bounces += data.bounces || 0;
      }
    }
    
    stats.push({
      screenName: screen,
      views: totalViews,
      uniqueUsers: uniqueUsers.size,
      avgTimeOnScreen: totalViews > 0 ? Math.round(totalTime / totalViews / 1000) : 0,
      bounceRate: totalViews > 0 ? Math.round(bounces / totalViews * 100) : 0
    });
  }
  
  return stats.sort((a, b) => b.views - a.views);
}

// ============================================================================
// SEARCH ANALYTICS
// ============================================================================

/**
 * Get search term analytics
 */
export async function getSearchAnalytics(days: number = 7, limitCount: number = 20): Promise<SearchAnalytics[]> {
  const searchTerms: Map<string, { count: number; results: number; clicks: number }> = new Map();
  
  for (let i = 0; i < days; i++) {
    const date = getDateString(getDaysAgo(i));
    const searchDoc = await getDoc(doc(navDb, ANALYTICS_COLLECTION, `search_terms_${date}`));
    
    if (searchDoc.exists()) {
      const data = searchDoc.data();
      Object.entries(data.terms || {}).forEach(([term, stats]: [string, any]) => {
        const existing = searchTerms.get(term) || { count: 0, results: 0, clicks: 0 };
        searchTerms.set(term, {
          count: existing.count + (stats.count || 0),
          results: existing.results + (stats.resultsFound || 0),
          clicks: existing.clicks + (stats.clicks || 0)
        });
      });
    }
  }
  
  const analytics: SearchAnalytics[] = [];
  
  searchTerms.forEach((stats, term) => {
    analytics.push({
      term,
      count: stats.count,
      resultsFound: stats.count > 0 ? Math.round(stats.results / stats.count) : 0,
      clickThroughRate: stats.count > 0 ? Math.round(stats.clicks / stats.count * 100) : 0
    });
  });
  
  return analytics
    .sort((a, b) => b.count - a.count)
    .slice(0, limitCount);
}

// ============================================================================
// REAL-TIME STATS
// ============================================================================

/**
 * Get real-time statistics
 */
export async function getRealtimeStats(): Promise<RealtimeStats> {
  // Get users active in last 5 minutes
  const fiveMinutesAgo = new Date();
  fiveMinutesAgo.setMinutes(fiveMinutesAgo.getMinutes() - 5);
  
  const recentEventsQuery = query(
    collection(navDb, EVENTS_COLLECTION),
    where('timestamp', '>=', Timestamp.fromDate(fiveMinutesAgo)),
    orderBy('timestamp', 'desc'),
    limit(100)
  );
  
  const recentEventsSnap = await getDocs(recentEventsQuery);
  const recentEvents = recentEventsSnap.docs.map(d => ({
    id: d.id,
    ...d.data()
  })) as AnalyticsEvent[];
  
  // Count unique active users
  const activeUserIds = new Set<string>();
  let activeNavigations = 0;
  
  recentEvents.forEach(event => {
    activeUserIds.add(event.userId);
    if (event.eventType === 'navigation_active') {
      activeNavigations++;
    }
  });
  
  return {
    activeUsers: activeUserIds.size,
    activeNavigations,
    recentEvents: recentEvents.slice(0, 20),
    updatedAt: Timestamp.now()
  };
}

// ============================================================================
// REPORT MANAGEMENT
// ============================================================================

/**
 * Create an analytics report configuration
 */
export async function createReport(
  report: Omit<AnalyticsReport, 'id' | 'createdAt'>,
  adminId: string
): Promise<string> {
  const docRef = await addDoc(collection(navDb, 'nav_reports'), {
    ...report,
    createdAt: Timestamp.now(),
    createdBy: adminId
  });
  
  return docRef.id;
}

/**
 * Get all report configurations
 */
export async function getReports(): Promise<AnalyticsReport[]> {
  const snapshot = await getDocs(collection(navDb, 'nav_reports'));
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as AnalyticsReport[];
}

/**
 * Generate a report
 */
export async function generateReport(reportId: string): Promise<Record<string, any>> {
  const reportDoc = await getDoc(doc(navDb, 'nav_reports', reportId));
  
  if (!reportDoc.exists()) {
    throw new Error('Report not found');
  }
  
  const report = reportDoc.data() as AnalyticsReport;
  const results: Record<string, any> = {
    reportName: report.name,
    generatedAt: new Date().toISOString(),
    metrics: {}
  };
  
  // Generate requested metrics
  if (report.metrics.includes('usage')) {
    results.metrics.usage = await getUsageMetrics();
  }
  if (report.metrics.includes('features')) {
    results.metrics.features = await getFeatureUsageStats();
  }
  if (report.metrics.includes('funnel')) {
    results.metrics.funnel = await getSignupFunnel();
  }
  if (report.metrics.includes('screens')) {
    results.metrics.screens = await getScreenAnalytics();
  }
  if (report.metrics.includes('search')) {
    results.metrics.search = await getSearchAnalytics();
  }
  
  // Update last run time
  await updateDoc(doc(navDb, 'nav_reports', reportId), {
    lastRunAt: Timestamp.now()
  });
  
  return results;
}

// ============================================================================
// EXPORT FUNCTIONS
// ============================================================================

/**
 * Export analytics data to CSV format
 */
export function exportToCsv(data: any[], filename: string): string {
  if (data.length === 0) return '';
  
  const headers = Object.keys(data[0]);
  const rows = data.map(row => 
    headers.map(header => {
      const value = row[header];
      // Escape quotes and wrap in quotes if contains comma
      if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    }).join(',')
  );
  
  return [headers.join(','), ...rows].join('\n');
}

/**
 * Get comprehensive analytics summary
 */
export async function getAnalyticsSummary(): Promise<{
  usage: UsageMetrics;
  features: FeatureUsage[];
  funnel: FunnelStep[];
  realtime: RealtimeStats;
  dauHistory: { date: string; count: number }[];
}> {
  const [usage, features, funnel, realtime, dauHistory] = await Promise.all([
    getUsageMetrics(),
    getFeatureUsageStats(7),
    getSignupFunnel(30),
    getRealtimeStats(),
    getDauHistory(14)
  ]);
  
  return {
    usage,
    features,
    funnel,
    realtime,
    dauHistory
  };
}
